import { useEffect, useState } from "react";
import { Loader2, User, Check } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const [username, setUsername] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user?.username) setUsername(user.username); }, [user?.username]);

  const dirty = username !== (user?.username || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/profile", { username });
      await checkAuth(); // refresh the handle shown in the nav
      toast.success("Username updated");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update username");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-xl">
        <div className="flex items-center gap-2">
          <User className="h-5 w-5 text-[color:var(--rb-accent)]" />
          <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">Profile</h1>
        </div>
        <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">
          Your <span className="text-[color:var(--rb-text)]">username</span> is the only thing shown publicly on the marketplace. Your real name and email stay private.
        </p>

        <div className="mt-6 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border border-[color:var(--rb-border)]">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-[color:var(--rb-surface)] text-[color:var(--rb-accent)]">{(user?.username || "U").slice(0, 1).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-sm text-[color:var(--rb-accent)]">@{user?.username || "…"}</div>
              <div className="truncate text-xs text-[color:var(--rb-text-faint)]">{user?.name} · {user?.email} <span className="text-[color:var(--rb-text-faint)]">(private)</span></div>
            </div>
          </div>

          <div className="mt-5">
            <label className="text-xs text-[color:var(--rb-text-faint)]">Username</label>
            <div className="mt-1 flex items-center gap-2">
              <span className="rb-mono text-[color:var(--rb-text-faint)]">@</span>
              <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="your-handle" data-testid="profile-username-input"
                className="rb-mono border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
              <Button onClick={save} disabled={saving || !dirty || !username.trim()} data-testid="profile-save-button"
                className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1.5 h-4 w-4" /> Save</>}
              </Button>
            </div>
            <p className="mt-2 text-xs text-[color:var(--rb-text-faint)]">3–20 characters: lowercase letters, numbers, or underscore. Changing it updates your name on everything you've published.</p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
